import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Tabs, Tab, Card, Table, TableHead, TableRow, TableCell,
  TableBody, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Switch, FormControlLabel, Chip, IconButton, MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import Layout from '../components/Layout';
import api from '../services/api';

const SECTIONS = ['Trade Categories', 'Payment Methods', 'Document Types', 'Project Statuses'];
const ENDPOINTS = ['trades', 'payment-methods', 'document-types', 'project-statuses'];

export default function Settings() {
  const [tab, setTab] = useState(0);
  const [data, setData] = useState({ trades: [], 'payment-methods': [], 'document-types': [], 'project-statuses': [] });
  const [dialog, setDialog] = useState({ open: false, item: null });
  const [form, setForm] = useState({});

  useEffect(() => {
    ENDPOINTS.forEach(ep => {
      api.get('/settings/' + ep).then(r => setData(prev => ({ ...prev, [ep]: r.data }))).catch(() => {});
    });
  }, []);

  const endpoint = ENDPOINTS[tab];
  const items = data[endpoint] || [];

  const handleOpen = (item) => {
    setForm(item || { name: '', description: '', is_active: true, color: '#1a237e', applies_to: 'project' });
    setDialog({ open: true, item: item || null });
  };

  const handleSave = async () => {
    try {
      if (dialog.item) {
        await api.put('/settings/' + endpoint + '/' + dialog.item.id, form);
      } else {
        await api.post('/settings/' + endpoint, form);
      }
      const r = await api.get('/settings/' + endpoint);
      setData(prev => ({ ...prev, [endpoint]: r.data }));
      setDialog({ open: false, item: null });
    } catch (e) { console.error(e); }
  };

  const handleToggle = async (item) => {
    await api.put('/settings/' + endpoint + '/' + item.id, { ...item, is_active: !item.is_active });
    const r = await api.get('/settings/' + endpoint);
    setData(prev => ({ ...prev, [endpoint]: r.data }));
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e' }}>Settings</Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>Manage master data and configuration</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen(null)}
            sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>
            Add Item
          </Button>
        </Box>

        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3, borderBottom: '1px solid #e8eaf6' }}>
          {SECTIONS.map((s) => (
            <Tab key={s} label={s} sx={{ fontWeight: 600, textTransform: 'none' }} />
          ))}
        </Tabs>

        <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f6fa' }}>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                {tab === 2 && <TableCell sx={{ fontWeight: 700 }}>Applies To</TableCell>}
                {tab === 3 && <TableCell sx={{ fontWeight: 700 }}>Color</TableCell>}
                <TableCell sx={{ fontWeight: 700, width: 120 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 110 }}>Actions</TableCell>
                <TableCell sx={{ width: '100%', p: 0 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(item => (
                <TableRow key={item.id} hover>
                  <TableCell sx={{ fontWeight: 600, py: 1 }}>{item.name}</TableCell>
                  <TableCell sx={{ color: '#666', fontSize: 13 }}>{item.description || '-'}</TableCell>
                  {tab === 2 && (
                    <TableCell>
                      <Chip label={item.applies_to} size="small" sx={{ bgcolor: '#e8eaf6', color: '#1a237e' }} />
                    </TableCell>
                  )}
                  {tab === 3 && (
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: item.color || '#1a237e' }} />
                        <Typography variant="caption">{item.color}</Typography>
                      </Box>
                    </TableCell>
                  )}
                  <TableCell>
                    <Chip label={item.is_active ? 'Active' : 'Inactive'} size="small"
                      sx={{ bgcolor: item.is_active ? '#e8f5e9' : '#ffebee',
                        color: item.is_active ? '#2e7d32' : '#c62828', fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <IconButton size="small" onClick={() => handleOpen(item)}>
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <Switch size="small" checked={item.is_active} onChange={() => handleToggle(item)} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '100%', p: 0 }} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Dialog open={dialog.open} onClose={() => setDialog({ open: false, item: null })}
          maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white', fontWeight: 700 }}>
            {dialog.item ? 'Edit' : 'Add'} {SECTIONS[tab]}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField fullWidth label="Name *" value={form.name || ''}
                onChange={e => setForm({ ...form, name: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              <TextField fullWidth label="Description" value={form.description || ''}
                onChange={e => setForm({ ...form, description: e.target.value })}
                multiline rows={2} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              {tab === 2 && (
                <TextField select fullWidth label="Applies To" value={form.applies_to || 'project'}
                  onChange={e => setForm({ ...form, applies_to: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                  {['project', 'contract', 'invoice', 'vendor'].map(v => (
                    <MenuItem key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</MenuItem>
                  ))}
                </TextField>
              )}
              {tab === 3 && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block' }}>Color</Typography>
                  <input type="color" value={form.color || '#1a237e'}
                    onChange={e => setForm({ ...form, color: e.target.value })}
                    style={{ width: '100%', height: 48, borderRadius: 8, border: '1px solid #e0e0e0', cursor: 'pointer' }} />
                </Box>
              )}
              <FormControlLabel
                control={<Switch checked={form.is_active !== false}
                  onChange={e => setForm({ ...form, is_active: e.target.checked })} />}
                label="Active" />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button onClick={() => setDialog({ open: false, item: null })}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} disabled={!form.name}
              sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>
              {dialog.item ? 'Save Changes' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}
